import { ReporteChequeo } from "../models/ReporteChequeo.ts";
import {
  calcularTiempoTrabajado,
  calcularTurno,
} from "../interfaces/formik/IReporteRelojChecador.ts";
import { formatDataForReport } from "./reporteChequeoUtils.ts";
import {
  operationalDayKeyForVisitRow,
  type BranchReportMeta,
} from "./operationalDayReporteChequeo.ts";
import * as moment from "moment-timezone";
import {
  dedupePayrollVisitsByPair,
  resolvePayrollDayRegisteredHours,
  shouldShowVariosRegistros,
  visitRegisteredHours,
} from "./payrollVariosDisplay.ts";

export type { BranchReportMeta };

export type PayrollVisitHourRow = {
  dateKey: string;
  fecha: string;
  entradaRegistrada: string;
  salidaRegistrada: string;
  horasRegistradas: number;
  branchId?: number;
  branchName?: string;
  turno?: string;
  positionName?: string;
  hoursInSchedule?: number;
  horasTrabajadasEnHorario?: number;
};

/** Pareo de checadas (reporte/nómina). Con nocturno, la fecha de fila es el día operativo. */
export function buildPayrollVisitHoursForPartner(
  records: ReporteChequeo[],
  partnerId: number,
  start: string,
  end: string,
  branchMeta?: Record<number, number> | Record<number, BranchReportMeta>
): PayrollVisitHourRow[] {
  const metaMap = branchMeta as Record<number, BranchReportMeta> | undefined;
  const rows = formatDataForReport(records, start, end, branchMeta).filter(
    (row) =>
      row.checkIn?.partner_id === partnerId && row.checkIn && row.checkOut
  );

  return rows.map((row) => {
    const entradaRegistrada = row.checkIn!.assistanceCheck_checkTime;
    const salidaRegistrada = row.checkOut!.assistanceCheck_checkTime;
    const dateKey = operationalDayKeyForVisitRow(row, metaMap, records);

    return {
      dateKey,
      fecha: moment.utc(dateKey).format("DD-MM-YYYY"),
      entradaRegistrada,
      salidaRegistrada,
      horasRegistradas: calcularTiempoTrabajado(
        entradaRegistrada,
        salidaRegistrada
      ),
      branchId: row.branch_id ?? row.checkIn?.branch_id,
      branchName:
        row.checkIn?.branch_name ||
        row.checkOut?.branch_name ||
        records.find(
          (r) =>
            Number(r.branch_id) ===
            Number(row.branch_id ?? row.checkIn?.branch_id)
        )?.branch_name,
      turno: calcularTurno(entradaRegistrada),
      positionName: row.checkIn?.position_name,
    };
  });
}

export function normalizePaymentDateKey(date: string): string {
  const m = moment(date, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : String(date);
}

function buildBranchNameById(
  records: ReporteChequeo[]
): Map<number, string> {
  const map = new Map<number, string>();
  (records || []).forEach((record) => {
    const branchId = Number(record.branch_id);
    if (Number.isFinite(branchId) && branchId > 0 && record.branch_name) {
      if (!map.has(branchId)) {
        map.set(branchId, record.branch_name);
      }
    }
  });
  return map;
}

export function enrichVisitsWithBranchNames(
  visits: PayrollVisitHourRow[],
  records: ReporteChequeo[]
): PayrollVisitHourRow[] {
  if (!visits.length) {
    return visits;
  }
  const byBranch = buildBranchNameById(records);
  return visits.map((visit) => ({
    ...visit,
    branchName:
      visit.branchName?.trim() ||
      (visit.branchId != null
        ? byBranch.get(Number(visit.branchId))
        : undefined) ||
      visit.branchName,
  }));
}

export const mapStoredVisit = (visit: any, dateKey: string): PayrollVisitHourRow => ({
  dateKey,
  fecha: moment.utc(dateKey).format("DD-MM-YYYY"),
  entradaRegistrada: visit.entradaRegistrada ?? visit.checkIn ?? "",
  salidaRegistrada: visit.salidaRegistrada ?? visit.checkOut ?? "",
  horasRegistradas: Number(
    visit.horasRegistradas ?? visit.hours ?? visit.horas ?? 0
  ),
  branchId: visit.branchId,
  branchName: visit.branchName,
  turno: visit.turno ?? calcularTurno(visit.entradaRegistrada ?? visit.checkIn ?? ""),
  positionName: visit.positionName,
  hoursInSchedule: visit.hoursInSchedule,
  horasTrabajadasEnHorario: visit.horasTrabajadasEnHorario,
});

/** Visitas de un día: API (visits) o pareo desde checadas reales. */
export function resolveVisitsForPaymentDay(
  day: any,
  partnerId: number,
  records: ReporteChequeo[],
  start: string,
  end: string,
  branchMeta?: Record<number, number> | Record<number, BranchReportMeta>
): PayrollVisitHourRow[] {
  const dateKey = normalizePaymentDateKey(day.date);
  if (Array.isArray(day.visits) && day.visits.length > 0) {
    return enrichVisitsWithBranchNames(
      day.visits.map((v: any) => mapStoredVisit(v, dateKey)),
      records
    );
  }
  if (!records?.length || !partnerId) {
    return [];
  }
  return enrichVisitsWithBranchNames(
    buildPayrollVisitHoursForPartner(
      records,
      partnerId,
      start,
      end,
      branchMeta
    ).filter((v) => v.dateKey === dateKey),
    records
  );
}

/** Una pasada sobre checadas del periodo; clave DD-MM-YYYY (fecha de fila). */
export function buildPartnerVisitHoursByFecha(
  records: ReporteChequeo[],
  partnerId: number,
  start: string,
  end: string,
  branchMeta?: Record<number, number> | Record<number, BranchReportMeta>
): Map<string, PayrollVisitHourRow[]> {
  const byFecha = new Map<string, PayrollVisitHourRow[]>();
  if (!records?.length || !partnerId) {
    return byFecha;
  }

  const all = enrichVisitsWithBranchNames(
    buildPayrollVisitHoursForPartner(records, partnerId, start, end, branchMeta),
    records
  );
  for (const visit of all) {
    const fecha = visit.fecha;
    if (!byFecha.has(fecha)) {
      byFecha.set(fecha, []);
    }
    byFecha.get(fecha)!.push(visit);
  }
  return byFecha;
}

export function resolveVisitsForChartDay(
  record: { fecha: string; visits?: any[] },
  partnerId: number,
  records: ReporteChequeo[],
  start: string,
  end: string,
  branchMeta?: Record<number, number> | Record<number, BranchReportMeta>,
  precomputedByFecha?: Map<string, PayrollVisitHourRow[]>
): PayrollVisitHourRow[] {
  const dateKey = moment(record.fecha, "DD-MM-YYYY", true).isValid()
    ? moment(record.fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
    : normalizePaymentDateKey(record.fecha);

  if (Array.isArray(record.visits) && record.visits.length > 0) {
    return enrichVisitsWithBranchNames(
      record.visits.map((v) => mapStoredVisit(v, dateKey)),
      records
    );
  }

  const cached = precomputedByFecha?.get(record.fecha);
  if (cached) {
    return cached;
  }

  if (!records?.length || !partnerId) {
    return [];
  }
  return enrichVisitsWithBranchNames(
    buildPayrollVisitHoursForPartner(
      records,
      partnerId,
      start,
      end,
      branchMeta
    ).filter((v) => v.dateKey === dateKey),
    records
  );
}

export function sumPayrollVisitHours(visits: PayrollVisitHourRow[]): number {
  return visits.reduce((acc, visit) => acc + visitRegisteredHours(visit), 0);
}

/** Une filas de pago del mismo día (suma horas como SumByDay en TableChequeo). */
export function mergeDailyPaymentsByDate(rows: any[]): any[] {
  if (!rows?.length) return [];

  const byDate = new Map<string, any[]>();
  rows.forEach((row) => {
    const key = normalizePaymentDateKey(row.date);
    if (!byDate.has(key)) {
      byDate.set(key, []);
    }
    byDate.get(key)!.push(row);
  });

  const merged: any[] = [];
  byDate.forEach((items, dateKey) => {
    if (items.length === 1) {
      merged.push(items[0]);
      return;
    }

    const sorted = [...items].sort((a, b) =>
      String(a.checkIn || "").localeCompare(String(b.checkIn || ""))
    );
    const totalHours = sorted.reduce(
      (sum, row) => sum + (Number(row.hours) || 0),
      0
    );
    const hoursInSchedule = sorted.reduce(
      (sum, row) => sum + (Number(row.hoursInSchedule) || 0),
      0
    );
    const payment = sorted.reduce(
      (sum, row) => sum + (Number(row.payment) || 0),
      0
    );
    const primary =
      [...sorted].sort(
        (a, b) => (Number(b.hours) || 0) - (Number(a.hours) || 0)
      )[0] || sorted[0];

    merged.push({
      ...primary,
      date: dateKey,
      hours: totalHours,
      hoursInSchedule,
      payment,
      checkIn: sorted[0].checkIn,
      checkOut: sorted[sorted.length - 1].checkOut,
      visitCount: sorted.length,
      visits: sorted.map((row) => ({
        entradaRegistrada: row.checkIn,
        salidaRegistrada: row.checkOut,
        horasRegistradas: Number(row.hours) || 0,
        hoursInSchedule: Number(row.hoursInSchedule) || 0,
        branchName: row.branchName || row.branch?.name,
        branchId: row.branchId ?? row.branch?.id,
      })),
    });
  });

  return merged.sort(
    (a, b) =>
      moment(normalizePaymentDateKey(a.date)).valueOf() -
      moment(normalizePaymentDateKey(b.date)).valueOf()
  );
}

/** Une filas detalladas del gráfico/registros por fecha. */
export function mergeChartDetailedByDate<
  T extends {
    fecha: string;
    entradaRegistrada?: string;
    salidaRegistrada?: string;
    horasRegistradas?: number;
    horasTrabajadasEnHorario?: number;
    horasExtras?: number;
    isAbsence?: boolean;
    isFreeDayEnjoyed?: boolean;
  },
>(rows: T[]): T[] {
  if (!rows?.length) return [];

  const special: T[] = [];
  const byFecha = new Map<string, T[]>();

  rows.forEach((row) => {
    if (row.isAbsence || row.isFreeDayEnjoyed) {
      special.push(row);
      return;
    }
    const key = moment(row.fecha, "DD-MM-YYYY", true).isValid()
      ? row.fecha
      : moment(row.fecha).format("DD-MM-YYYY");
    if (!byFecha.has(key)) {
      byFecha.set(key, []);
    }
    byFecha.get(key)!.push(row);
  });

  const merged: T[] = [...special];
  byFecha.forEach((items, fecha) => {
    if (items.length === 1) {
      merged.push(items[0]);
      return;
    }

    const sorted = [...items].sort((a, b) =>
      String(a.entradaRegistrada || "").localeCompare(
        String(b.entradaRegistrada || "")
      )
    );
    const visitRows = sorted.map((r) => ({
      dateKey: fecha,
      fecha,
      entradaRegistrada: r.entradaRegistrada || "",
      salidaRegistrada: r.salidaRegistrada || "",
      horasRegistradas: Number(r.horasRegistradas ?? 0),
      branchName: (r as any).branchName,
      branchId: (r as any).branchId,
      turno: (r as any).turno,
      horasTrabajadasEnHorario: Number(r.horasTrabajadasEnHorario ?? 0),
      checkIn: (r as any).checkIn,
      checkOut: (r as any).checkOut,
    }));
    const dedupedVisits = dedupePayrollVisitsByPair(visitRows);
    const variosRegistros = shouldShowVariosRegistros(dedupedVisits);
    const base = { ...sorted[0] };
    const firstVisit = dedupedVisits[0];
    const lastVisit = dedupedVisits[dedupedVisits.length - 1] ?? firstVisit;
    const entradaMerged =
      firstVisit?.entradaRegistrada ?? sorted[0].entradaRegistrada;
    const salidaMerged =
      lastVisit?.salidaRegistrada ??
      sorted[sorted.length - 1].salidaRegistrada;
    const horasMerged = resolvePayrollDayRegisteredHours(dedupedVisits, {
      entradaRegistrada: entradaMerged,
      salidaRegistrada: salidaMerged,
    });

    merged.push({
      ...base,
      fecha,
      entradaRegistrada: entradaMerged,
      salidaRegistrada: salidaMerged,
      horasRegistradas: horasMerged,
      horasTrabajadasEnHorario: dedupedVisits.reduce(
        (s, r) => s + Number(r.horasTrabajadasEnHorario ?? 0),
        0
      ),
      horasExtras: sorted.reduce((s, r) => s + Number(r.horasExtras ?? 0), 0),
      visitCount: dedupedVisits.length,
      variosRegistros,
      visits: dedupedVisits.map((r) => ({
        entradaRegistrada: r.entradaRegistrada,
        salidaRegistrada: r.salidaRegistrada,
        horasRegistradas: r.horasRegistradas,
        horasTrabajadasEnHorario: r.horasTrabajadasEnHorario,
        branchName: r.branchName,
        branchId: r.branchId,
        turno: r.turno,
      })),
    } as T);
  });

  return merged.sort(
    (a, b) =>
      moment(a.fecha, "DD-MM-YYYY").valueOf() -
      moment(b.fecha, "DD-MM-YYYY").valueOf()
  );
}
